import React, { useEffect } from 'react';
import { Route, Switch } from 'react-router-dom';
import cn from 'classnames';

import STORE from 'config/store.singleton';
import ELID from 'constants/elid.const';
import fromObjectToDataAttributes from 'utils/converters/object/fromObjectToDataAttributes.util';

import Activity from 'activity/components/Activity.component';
import BookingRouter from 'booking/BookingRouter.component';
import { AddNewClient } from 'clientProfile/components/AddNewClient.component';
import { ClientProfileRedirect } from 'scheduler/ClientProfileRedirect.component';
import Scheduler from 'scheduler/components/Scheduler';
import NoAccessOverlay from 'scheduler/NoAccessOverlay.component';

const SchedulerRouter = ({ dataAttributes }) => {
  useEffect(() => {
    // FIXME: should use regular React for toggle of element classes

    const parent = document.getElementById(ELID.schedscroll);

    if (!parent) return;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        // I would like to specify by classname like this
        // (mutation.addedNodes.length && mutation.addedNodes[0].nodName !== bannedEl)
        // but bannedEl is null and I can't exclude it from the node child list, so I select them by tag name
        // If we want a booking window do not overlap the scheduler, wrap it in tag <section>

        if (mutation.addedNodes.length && mutation.addedNodes[0].tagName !== 'DIV') {
          parent.classList.add('bookingIsOpen');
          parent.classList.add('bookingIsOpenFromCheckout');
        }

        if (mutation.removedNodes.length) {
          parent.classList.remove('bookingIsOpen');
          parent.classList.remove('bookingIsOpenFromCheckout');
        }
      }
    });

    observer.observe(parent, { childList: true });

    // parent.children is HTMLCollection, they are not array, so they need to be converted into array.
    // when booking from checkout, parent don't have class 'bookingIsOpen'
    const children = [...parent.children];
    const bookingStepsChildren = children.map(({ classList }) => !!classList.contains('booking-steps'));
    const fomCheckoutChildren = children.map(({ classList }) => !!classList.contains('booking-steps-from-checkout'));

    if (bookingStepsChildren.length > 0 && bookingStepsChildren.includes(true)) {
      parent.classList.add('bookingIsOpen');
    } else if (parent.classList.contains('bookingIsOpen')) {
      parent.classList.remove('bookingIsOpen');
    }

    if (fomCheckoutChildren.length > 0 && fomCheckoutChildren.includes(true)) {
      parent.classList.add('bookingIsOpenFromCheckout');
    } else if (parent.classList.contains('bookingIsOpenFromCheckout')) {
      parent.classList.remove('bookingIsOpenFromCheckout');
    }

    return () => observer.disconnect();
  }, []);

  const currentSalon = STORE.getState()?.currentSalon;

  const salonId = currentSalon?.id;
  const calendarMode = currentSalon?.calendar_mode;
  const hasNewSalonAccess = currentSalon?.has_new_salon_access;
  const hasMissedInvoice = currentSalon?.has_missed_invoice;
  const betAnnualSalon = currentSalon?.beta_annual_salon;

  const isLightMode = calendarMode === 'light';
  const isDarkMode = calendarMode === 'dark';
  const isBrightMode = !isLightMode && !isDarkMode;

  return (
    <div
      data-bem="SchedulerRouter"
      id={ELID.schedscroll}
      className={cn({
        'scheduler-container': true,
        'light-class': isLightMode,
        'dark-class': isDarkMode,
        'bright-class': isBrightMode,
      })}
      {...fromObjectToDataAttributes(dataAttributes)}
      {...fromObjectToDataAttributes({ salonId, calendarMode, hasNewSalonAccess, hasMissedInvoice })}
    >
      {(hasMissedInvoice && !betAnnualSalon) || !hasNewSalonAccess ? <NoAccessOverlay trial={hasNewSalonAccess} unpaid={hasMissedInvoice} billingLink={`/salon/${salonId}/settings/billing`} /> : null}

      <Route path="/salon/:id/week/:dateString?/:stylistId" render={(props) => <Scheduler key={props.match.params.stylistId} {...props} />} />
      <Route path="/salon/:id/day/:dateString?/:activeDateString?" component={Scheduler} />
      {/* Render both the scheduler and the booking window if we are creating a booking */}
      <Route path="/salon/:id/:view?/:dateString?/:stylistId?/feed" component={Activity} />

      {/* Use Switch to ensure only one route matches - more specific routes first */}
      <Switch>
        {/* Client profile routes from appointment/new - must come before the base appointment/new route */}
        <Route path="/salon/:id/day/:dateString?/appointment/new/client/:client_id" component={ClientProfileRedirect} />
        <Route path="/salon/:id/week/:dateString?/:stylistId?/appointment/new/client/:client_id" component={ClientProfileRedirect} />

        {/* Client profile routes from appointment/edit - must come before the base appointment/edit route */}
        <Route path="/salon/:id/day/:dateString?/appointment/edit/:appointmentId/client/:client_id" component={ClientProfileRedirect} />
        <Route path="/salon/:id/week/:dateString?/:stylistId/appointment/edit/:appointmentId/client/:client_id" component={ClientProfileRedirect} />

        {/* Legacy add new client routes */}
        <Route path="/salon/:id/day/:dateString?/appointment/edit/:appointmentId/client" component={AddNewClient} />
        <Route path="/salon/:id/week/:dateString?/:stylistId/appointment/edit/:appointmentId/client" component={AddNewClient} />

        {/* Base appointment and booking routes - these will match any path under them */}
        <Route path="/salon/:id/day/:dateString?/appointment/new" component={BookingRouter} />
        <Route path="/salon/:id/week/:dateString?/:stylistId?/appointment/new" component={BookingRouter} />
        <Route path="/salon/:id/day/:dateString?/create/new/client/new/" component={BookingRouter} />
        <Route path="/salon/:id/week/:dateString?/:stylistId?/create/new/client/new/" component={BookingRouter} />
        <Route path="/salon/:id/day/:dateString?/appointment/edit/:appointmentId" component={BookingRouter} />
        <Route path="/salon/:id/week/:dateString?/:stylistId?/appointment/edit/:appointmentId" component={BookingRouter} />
        <Route path="/salon/:id/day/:dateString?/event/edit/:eventId" component={BookingRouter} />
        <Route path="/salon/:id/week/:dateString?/:stylistId?/event/edit/:eventId" component={BookingRouter} />
      </Switch>

      {/* Direct client profile routes - open sidebar */}
      <Switch>
        <Route path="/salon/:id/day/:dateString/client/edit/:client_id" component={ClientProfileRedirect} />
        <Route path="/salon/:id/week/:dateString/:stylistId/client/edit/:client_id" component={ClientProfileRedirect} />
        <Route path="/salon/:id/day/:dateString/client/:client_id" component={ClientProfileRedirect} />
        <Route path="/salon/:id/week/:dateString/:stylistId/client/:client_id" component={ClientProfileRedirect} />
      </Switch>
    </div>
  );
};

export default SchedulerRouter;
